/**
 * libraryImport - best-effort translation of external shape libraries into
 * LibreDraw stencils.
 *
 * Supports:
 *  - LibreDraw native export:  { name, stencils: [{ label, kind, elements:[...] }] }
 *  - Excalidraw v2 libraries:  { type:"excalidrawlib", libraryItems:[{ elements:[...] }] }
 *  - Excalidraw v1 libraries:  { library: [ [elements...], ... ] }
 *
 * Excalidraw's element model is richer than ours, so this maps the shapes we
 * support (rectangle/ellipse/diamond/text) and skips the rest (arrows, freedraw,
 * images, etc.). Positions are made relative to each item's bounding-box center.
 */
import { shapePoly, shapePath, PATH_KINDS } from "./canvas/geometry";

export interface StencilEl {
  kind: string; dx: number; dy: number; w: number; h: number;
  fill?: string; stroke?: string; strokeWidth?: number; text?: string;
  points?: number[];  // flat [x,y,...] relative to (dx,dy) - for kind "draw" (line/arrow/freedraw)
  closed?: boolean;   // filled/closed polygon (Excalidraw line with a fill or a looped path)
  smooth?: boolean;   // freehand stroke → render with tension; line/arrow → sharp
  opacity?: number;   // 0..1
  fontSize?: number;  // text size (kind "text")
  align?: string;     // text align: left|center|right
  src?: string;       // image data URL (kind "image")
}
export interface Stencil { label: string; kind: string; elements: StencilEl[]; }
export interface ImportedLibrary { name: string; stencils: Stencil[]; }

// Excalidraw types → our stencil kinds. Freehand/line/arrow become "draw" polylines so
// hand-drawn icons keep their strokes instead of vanishing.
const mapType = (t: string): string | null => {
  switch (t) {
    case "rectangle": return "rectangle";
    case "ellipse": return "ellipse";
    case "diamond": return "diamond";
    case "text": return "text";
    case "line": case "arrow": case "freedraw": return "draw";
    case "image": return "image";
    default: return null; // frame / embeddable - skipped
  }
};

function itemToStencil(elements: any[], idx: number, name?: string, libName?: string, files?: any): Stencil | null {
  if (!Array.isArray(elements)) return null;
  const shapes = elements.filter((e) => e && mapType(e.type) && !(e.type === "image" && !(files && files[e.fileId])));
  if (!shapes.length) return null;
  const minX = Math.min(...shapes.map((e) => e.x ?? 0));
  const minY = Math.min(...shapes.map((e) => e.y ?? 0));
  const maxX = Math.max(...shapes.map((e) => (e.x ?? 0) + (e.width ?? 0)));
  const maxY = Math.max(...shapes.map((e) => (e.y ?? 0) + (e.height ?? 0)));
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  const els: StencilEl[] = shapes.map((e) => {
    const kind = mapType(e.type)!;
    const hasFill = !!(e.backgroundColor && e.backgroundColor !== "transparent" && e.fillStyle !== "none");
    const base: StencilEl = {
      kind,
      dx: (e.x ?? 0) - cx,
      dy: (e.y ?? 0) - cy,
      w: Math.max(1, e.width ?? 40),
      h: Math.max(1, e.height ?? 40),
      fill: hasFill ? e.backgroundColor : (kind === "draw" ? "transparent" : "#eef2ff"),
      stroke: e.strokeColor || "#1f2430",
      strokeWidth: Math.max(1, Math.round(e.strokeWidth ?? 2)),
      text: e.text || undefined,
      opacity: typeof e.opacity === "number" ? Math.max(0, Math.min(1, e.opacity / 100)) : 1,
    };
    if (kind === "draw" && Array.isArray(e.points) && e.points.length > 1) {
      const pts = e.points;
      base.points = pts.flatMap((p: any) => [p[0] ?? 0, p[1] ?? 0]);
      const looped = pts.length > 2 && Math.hypot((pts[0][0] ?? 0) - (pts[pts.length - 1][0] ?? 0), (pts[0][1] ?? 0) - (pts[pts.length - 1][1] ?? 0)) < 4;
      base.closed = (e.type === "line" || e.type === "freedraw") && (looped || hasFill);
      base.smooth = e.type === "freedraw";
    }
    if (kind === "text") {
      base.fontSize = e.fontSize || 20;             // Excalidraw text color is the STROKE color
      base.align = e.textAlign || "left";
      base.stroke = e.strokeColor || "#1f2430";
    }
    if (kind === "image" && files && files[e.fileId]) {
      base.src = files[e.fileId].dataURL || files[e.fileId];
    }
    return base;
  });
  const label = (name && String(name).trim()) || (libName && String(libName).trim()) || `Item ${idx + 1}`;
  return { label, kind: els[0].kind, elements: els };
}

export function parseLibrary(text: string, libName?: string): ImportedLibrary {
  let data: any;
  try { data = JSON.parse(text); } catch { return { name: "Invalid file", stencils: [] }; }

  // Native LibreDraw export
  if (data && Array.isArray(data.stencils)) {
    return { name: data.name || libName || "Imported", stencils: data.stencils };
  }

  const topFiles = data?.files || {};
  let items: { els: any[]; name?: string; files?: any }[] = [];
  if (Array.isArray(data?.libraryItems)) items = data.libraryItems.map((it: any) => ({ els: it.elements || it, name: it.name, files: { ...topFiles, ...(it.files || {}) } }));
  else if (Array.isArray(data?.library)) items = data.library.map((els: any) => ({ els, files: topFiles }));

  const stencils = items
    .map((it, i) => itemToStencil(it.els, i, it.name, libName, it.files))
    .filter((s): s is Stencil => s !== null);
  return { name: data?.name || libName || "Imported library", stencils };
}

// ---- public Excalidraw library catalog (libraries.excalidraw.com) ----
// The catalog JSON and the per-library files/previews under /libraries/ are all CORS-enabled,
// so we can browse + import entirely client-side (works for anonymous users, no backend proxy).
const LIB_BASE = "https://libraries.excalidraw.com/";
export interface CatalogItem { name: string; description: string; author: string; source: string; preview: string; id: string; }

export async function fetchExcalidrawCatalog(): Promise<CatalogItem[]> {
  try {
    const r = await fetch(LIB_BASE + "libraries.json");
    if (!r.ok) return [];
    const arr = await r.json();
    if (!Array.isArray(arr)) return [];
    return arr.map((e: any) => ({
      name: e.name || "Untitled",
      description: e.description || "",
      author: (e.authors && e.authors[0] && e.authors[0].name) || "",
      source: e.source || "",
      preview: e.preview ? LIB_BASE + "libraries/" + e.preview : "",
      id: String(e.id || e.source || e.name || Math.random()),
    })).filter((e: CatalogItem) => !!e.source);
  } catch { return []; }
}

export async function fetchExcalidrawLibrary(source: string, name: string): Promise<ImportedLibrary> {
  try {
    const r = await fetch(LIB_BASE + "libraries/" + source);
    if (!r.ok) return { name, stencils: [] };
    const lib = parseLibrary(await r.text(), name);
    return { name: name || lib.name, stencils: lib.stencils };
  } catch { return { name, stencils: [] }; }
}

// Render a stencil's elements to a small SVG data-URI for the library tile preview,
// so imported shapes show their ACTUAL geometry instead of a generic rectangle.
const escXml = (s: string) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
// Guard imported colors before they reach SVG attributes - an
// imported .excalidrawlib color could otherwise break out of the attribute into markup.
const safeColor = (c?: string): string =>
  c && /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([\d.,%\s]+\))$/.test(c.trim()) ? c.trim() : "#000000";

export function stencilToSvg(elements: StencilEl[], size = 46): string {
  if (!elements || !elements.length) return "";
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const bump = (x: number, y: number) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  for (const el of elements) {
    if (el.kind === "draw" && el.points) { for (let i = 0; i + 1 < el.points.length; i += 2) bump(el.dx + el.points[i], el.dy + el.points[i + 1]); }
    else { bump(el.dx, el.dy); bump(el.dx + el.w, el.dy + el.h); }
  }
  if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 1; maxY = 1; }
  const bw = Math.max(1, maxX - minX), bh = Math.max(1, maxY - minY);
  const pad = 3, s = Math.min((size - pad * 2) / bw, (size - pad * 2) / bh);
  const ox = pad + (size - pad * 2 - bw * s) / 2 - minX * s;
  const oy = pad + (size - pad * 2 - bh * s) / 2 - minY * s;
  const tx = (x: number) => (x * s + ox).toFixed(2), ty = (y: number) => (y * s + oy).toFixed(2);
  const parts: string[] = [];
  for (const el of elements) {
    const op = el.opacity ?? 1;
    const stroke = safeColor(el.stroke || "#1f2430");
    const sw = Math.max(0.5, (el.strokeWidth || 1) * s * 0.9).toFixed(2);
    const fill = el.fill && el.fill !== "transparent" ? safeColor(el.fill) : "none";
    if (el.kind === "draw" && el.points && el.points.length > 1) {
      const pd: string[] = []; for (let i = 0; i + 1 < el.points.length; i += 2) pd.push(`${tx(el.dx + el.points[i])},${ty(el.dy + el.points[i + 1])}`);
      const tag = el.closed ? "polygon" : "polyline";
      parts.push(`<${tag} points="${pd.join(" ")}" fill="${el.closed ? fill : "none"}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round" opacity="${op}"/>`);
    } else if (el.kind === "ellipse") {
      parts.push(`<ellipse cx="${tx(el.dx + el.w / 2)}" cy="${ty(el.dy + el.h / 2)}" rx="${(el.w * s / 2).toFixed(2)}" ry="${(el.h * s / 2).toFixed(2)}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
    } else if (el.kind === "diamond") {
      const cx = el.dx + el.w / 2, cy = el.dy + el.h / 2;
      parts.push(`<polygon points="${tx(cx)},${ty(el.dy)} ${tx(el.dx + el.w)},${ty(cy)} ${tx(cx)},${ty(el.dy + el.h)} ${tx(el.dx)},${ty(cy)}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
    } else if (el.kind === "image" && el.src) {
      parts.push(`<image x="${tx(el.dx)}" y="${ty(el.dy)}" width="${(el.w * s).toFixed(2)}" height="${(el.h * s).toFixed(2)}" href="${el.src}" opacity="${op}"/>`);
    } else if (el.kind === "text") {
      const fs = Math.max(3, (el.fontSize || 20) * s);
      const anchor = el.align === "center" ? "middle" : el.align === "right" ? "end" : "start";
      const ax = el.align === "center" ? el.dx + el.w / 2 : el.align === "right" ? el.dx + el.w : el.dx;
      parts.push(`<text x="${tx(ax)}" y="${ty(el.dy + (el.h || el.fontSize || 16) * 0.8)}" font-family="Inter,system-ui,sans-serif" font-weight="600" font-size="${fs.toFixed(1)}" fill="${stroke}" text-anchor="${anchor}" opacity="${op}">${escXml(el.text || "")}</text>`);
    } else if (el.kind === "cylinder") {
      const ry = Math.min(el.h * 0.16, 16);
      parts.push(`<rect x="${tx(el.dx)}" y="${ty(el.dy + ry)}" width="${(el.w * s).toFixed(2)}" height="${((el.h - 2 * ry) * s).toFixed(2)}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
      parts.push(`<ellipse cx="${tx(el.dx + el.w / 2)}" cy="${ty(el.dy + el.h - ry)}" rx="${(el.w * s / 2).toFixed(2)}" ry="${(ry * s).toFixed(2)}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
      parts.push(`<ellipse cx="${tx(el.dx + el.w / 2)}" cy="${ty(el.dy + ry)}" rx="${(el.w * s / 2).toFixed(2)}" ry="${(ry * s).toFixed(2)}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
    } else if (PATH_KINDS.has(el.kind)) {
      // document/class/package/cube/cloud - the same path the canvas draws, scaled into the tile
      const d = shapePath(el.kind, el.w, el.h);
      parts.push(`<path transform="translate(${ox.toFixed(2)} ${oy.toFixed(2)}) scale(${s.toFixed(4)}) translate(${el.dx} ${el.dy})" d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${((el.strokeWidth || 1) * 0.9).toFixed(2)}" stroke-linejoin="round" opacity="${op}"/>`);
    } else {
      // triangle / hexagon / parallelogram → their real polygon; anything else → a box
      const poly = shapePoly({ kind: el.kind, w: el.w, h: el.h } as any);
      if (poly) {
        const pts = poly.map(([px, py]) => `${tx(el.dx + px)},${ty(el.dy + py)}`).join(" ");
        parts.push(`<polygon points="${pts}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round" opacity="${op}"/>`);
      } else {
        parts.push(`<rect x="${tx(el.dx)}" y="${ty(el.dy)}" width="${(el.w * s).toFixed(2)}" height="${(el.h * s).toFixed(2)}" rx="2" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" opacity="${op}"/>`);
      }
    }
  }
  return `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${parts.join("")}</svg>`)}`;
}

/** Strip anything executable or remote from an untrusted SVG before it becomes a data URI.
 *  Icons render through an <img>, where scripts cannot run anyway, but a document should never
 *  carry script or event handlers, and an external reference would leak a request per render. */
function cleanSvg(src: string): string {
  return src
    .replace(/<\?xml[^>]*\?>/gi, "")
    .replace(/<!DOCTYPE[\s\S]*?>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<foreignObject[\s\S]*?<\/foreignObject>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .trim();
}

/** An icon's placed size, taken from the SVG's viewBox (or width/height), scaled so its longest
 *  side is `box` and its aspect ratio is preserved - so a wide logo does not arrive as a square. */
function svgSize(src: string, box = 96): { w: number; h: number } {
  const fit = (w: number, h: number) => {
    if (!(w > 0 && h > 0)) return { w: box, h: box };
    const k = box / Math.max(w, h);
    return { w: Math.round(w * k), h: Math.round(h * k) };
  };
  const vb = /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(src);
  if (vb) return fit(parseFloat(vb[1]), parseFloat(vb[2]));
  const w = /\bwidth\s*=\s*["']([\d.]+)/i.exec(src), h = /\bheight\s*=\s*["']([\d.]+)/i.exec(src);
  if (w && h) return fit(parseFloat(w[1]), parseFloat(h[1]));
  return { w: box, h: box };
}

/** Turn a standalone .svg file into a library stencil. The icon becomes an `image` element
 *  carrying the SVG as a data URI, so it reuses everything the canvas already does with images -
 *  move, scale, rotate, export, persist - with no new element kind and no SVG path parser. It
 *  stays vector, so it is crisp at any zoom. Returns null if the text is not an SVG. */
export function svgToStencil(svgText: string, label: string): Stencil | null {
  const clean = cleanSvg(svgText || "");
  if (!/<svg[\s>]/i.test(clean)) return null;
  const { w, h } = svgSize(clean);
  const src = `data:image/svg+xml;utf8,${encodeURIComponent(clean)}`;
  return { label: label || "Icon", kind: "image", elements: [{ kind: "image", dx: 0, dy: 0, w, h, src }] };
}

/** Read several files into one library. Icon packs download as one .svg per icon, so a
 *  multi-select imports as a single icon set rather than one library per file. */
export function readLibraryFiles(files: File[], cb: (r: ImportedLibrary) => void) {
  const list = Array.from(files || []);
  if (!list.length) { cb({ name: "Import failed", stencils: [] }); return; }
  const results: ImportedLibrary[] = new Array(list.length);
  let left = list.length;
  list.forEach((f, i) => readLibraryFile(f, (r) => {
    results[i] = r;
    if (--left === 0) {
      const stencils = results.flatMap((r) => (r && r.stencils) || []);
      cb({ name: list.length === 1 ? (results[0]?.name || "Library") : "Icons", stencils });
    }
  }));
}

export function readLibraryFile(file: File, cb: (r: ImportedLibrary) => void) {
  const rd = new FileReader();
  const named = (file.name || "").replace(/\.svg$/i, "").replace(/[-_]+/g, " ").trim();
  rd.onload = () => {
    const txt = (rd.result as string) || "";
    try {
      // a plain .svg is an icon, not an Excalidraw library
      if (/\.svg$/i.test(file.name || "") || file.type === "image/svg+xml" || /^\s*<svg[\s>]/i.test(txt)) {
        const st = svgToStencil(txt, named || "Icon");
        cb(st ? { name: named || "Icon", stencils: [st] } : { name: "Import failed", stencils: [] });
        return;
      }
      cb(parseLibrary(txt));
    } catch { cb({ name: "Import failed", stencils: [] }); }
  };
  rd.readAsText(file);
}
